import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@repo/ui";
import { PrinterIcon, Trash2Icon } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";

import { SearchableSelect } from "@/components/searchable-select";
import {
  EXPO_48_CHARACTERISTICS,
  EXPO_48_DEVELOPERS,
  EXPO_48_INTERESTS,
  formatPriceWithCommas,
} from "@/lib/expo-48-constants";
import type { Expo48FormData } from "@/lib/expo-48-schema";

interface PropertyPurchaseCardProps {
  form: UseFormReturn<Expo48FormData>;
  index: number;
  onRemove?: () => void;
  canRemove: boolean;
  propertyPurchaseId?: number;
  couponReadyAt?: string | null;
  couponsPrintedAt?: string | null;
  onPrint?: () => void;
}

export default function PropertyPurchaseCard({
  form,
  index,
  onRemove,
  canRemove,
  propertyPurchaseId,
  couponReadyAt,
  couponsPrintedAt,
  onPrint,
}: PropertyPurchaseCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Purchase #{index + 1}</CardTitle>
          <div className="flex items-center gap-2">
            {propertyPurchaseId && onPrint && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!couponReadyAt}
                  onClick={onPrint}
                  title={!couponReadyAt ? "Coupons not ready yet" : "Print coupons"}
                >
                  <PrinterIcon className="size-4" />
                  Print
                </Button>
                {couponsPrintedAt && (
                  <span className="text-sm text-muted-foreground">พิมพ์ไปแล้ว</span>
                )}
              </>
            )}
            {canRemove && onRemove && (
              <Button type="button" variant="destructive" size="sm" onClick={onRemove}>
                <Trash2Icon className="size-4" />
                Remove
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name={`purchases.${index}.fullAddress`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Address ที่อยู่เต็ม *</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="บ้านเลขที่ ถนน ตำบล อำเภอ จังหวัด รหัสไปรษณีย์"
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`purchases.${index}.purchasePrice`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>ราคาซื้อ / Actual purchase price *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="เช่น 3,500,000"
                  inputMode="numeric"
                  onChange={(e) => {
                    const formatted = formatPriceWithCommas(e.target.value);
                    field.onChange(formatted);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`purchases.${index}.developer`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>ผู้ประกอบการ / Developer *</FormLabel>
              <FormControl>
                <SearchableSelect
                  items={EXPO_48_DEVELOPERS}
                  value={field.value || ""}
                  onChange={field.onChange}
                  placeholder="เลือกผู้ประกอบการ"
                  searchPlaceholder="ค้นหาผู้ประกอบการ..."
                  emptyText="ไม่พบผู้ประกอบการ"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`purchases.${index}.purchasedPropertyType`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>ประเภทอสังหาริมทรัพย์ที่ซื้อ / Purchased Property Type *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกประเภทอสังหาริมทรัพย์" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {EXPO_48_INTERESTS.filter(
                    (i) => i.value !== "furniture" && i.value !== "other"
                  ).map((interest) => (
                    <SelectItem key={interest.value} value={interest.value}>
                      {interest.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`purchases.${index}.purchasedPropertyCharacteristics`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                ลักษณะที่อยู่อาศัยที่ซื้อ / Purchased Property Characteristics *
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกลักษณะที่อยู่อาศัย" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {EXPO_48_CHARACTERISTICS.map((char) => (
                    <SelectItem key={char.value} value={char.value}>
                      {char.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}
